// src/components/Debug/AuthDiagnostic.jsx
import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent,
  Alert,
  Divider
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const AuthDiagnostic = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loading, error } = useAuth();

  const diagnosticInfo = {
    currentPath: location.pathname,
    currentSearch: location.search,
    isAuthenticated,
    hasUser: !!user,
    loading,
    error,
    userEmail: user?.email || 'Not available',
    timestamp: new Date().toISOString()
  };

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        🔧 Authentication Diagnostic
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        This diagnostic tool helps identify sign-in issues. Use the buttons below to test navigation.
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Status
          </Typography>
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Typography><strong>Current Path:</strong> {diagnosticInfo.currentPath}</Typography>
            <Typography><strong>Search Params:</strong> {diagnosticInfo.currentSearch || 'None'}</Typography>
            <Typography><strong>Is Authenticated:</strong> {diagnosticInfo.isAuthenticated ? '✅ Yes' : '❌ No'}</Typography>
            <Typography><strong>Has User:</strong> {diagnosticInfo.hasUser ? '✅ Yes' : '❌ No'}</Typography>
            <Typography><strong>Loading:</strong> {diagnosticInfo.loading ? '⏳ Yes' : '✅ No'}</Typography>
            <Typography><strong>Error:</strong> {diagnosticInfo.error || '✅ None'}</Typography>
            <Typography><strong>User Email:</strong> {diagnosticInfo.userEmail}</Typography>
          </Box>
        </CardContent>
      </Card>

      <Typography variant="h6" gutterBottom>
        Navigation Tests
      </Typography>
      
      <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
        <Button 
          variant="contained" 
          onClick={() => navigate('/auth')}
          color="primary"
        >
          Go to Sign In (/auth)
        </Button>
        
        <Button 
          variant="contained" 
          onClick={() => navigate('/auth?mode=signin')}
          color="secondary"
        >
          Go to Sign In with Mode (/auth?mode=signin)
        </Button>
        
        <Button 
          variant="contained" 
          onClick={() => navigate('/auth?mode=signup')}
          color="success"
        >
          Go to Sign Up (/auth?mode=signup)
        </Button>
        
        <Button 
          variant="outlined" 
          onClick={() => navigate('/movies')}
        >
          Go to Movies (Public)
        </Button>
        
        <Button 
          variant="outlined" 
          onClick={() => navigate('/feed')}
        >
          Go to Feed (Protected)
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Quick Actions
      </Typography>
      
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Button 
          variant="outlined" 
          onClick={() => window.location.reload()}
          color="warning"
        >
          🔄 Reload Page
        </Button>
        
        <Button 
          variant="outlined" 
          onClick={() => {
            localStorage.clear();
            sessionStorage.clear();
            window.location.reload();
          }}
          color="error"
        >
          🗑️ Clear All Cache & Reload
        </Button>
      </Box>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="caption" component="pre">
          {JSON.stringify(diagnosticInfo, null, 2)}
        </Typography>
      </Box>
    </Box>
  );
};

export default AuthDiagnostic;